/**
 * useLabel — i18n wrapper + 백엔드 fw_label 로더 훅.
 *
 * 【역할】
 * - react-i18next 의 `t` 를 항상 string 반환형으로 래핑 (ReactNode 혼합 금지).
 * - 백엔드 fw_label 테이블을 현재 locale 에 대해 i18n resource bundle 로 주입.
 *
 * 【주요 기능】
 * - label(key, fallback?): 번역 문자열 (미존재 시 fallback 또는 key 그대로)
 * - loadLabels(locale?): /labels?locale=xx 호출 → addResourceBundle
 * - i18n 인스턴스 직접 노출 (전환/이벤트용)
 *
 * 【사용 예시】
 * ```tsx
 * const { t, loadLabels } = useLabel()
 * await loadLabels('ko')
 * return <Button>{t('BTN_SAVE', '저장')}</Button>
 * ```
 *
 * 【왜 이 패턴?】
 * - vue-spring-fw 의 `useLabel` composable 포팅. 프런트 JSON 번역 파일 + DB 라벨 이원화 지원.
 * - 사용자 추가 라벨(DB) 이 빌드 시점 JSON 에 없어도 런타임에 즉시 반영.
 */
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import http from './api/axios'

/**
 * useLabel — 번역 헬퍼 + DB 라벨 로더.
 * @param ns - i18next namespace (기본 'translation'). 도메인별 분리 시 지정.
 * @returns { t, label, loadLabels, i18n } — t 와 label 은 동일 함수.
 */
export function useLabel(ns?: string) {
  const { t, i18n } = useTranslation(ns)

  /**
   * 번역 문자열 반환 — 항상 string 을 보장 (t 가 object 반환하면 toString).
   * @param key - 번역 키 (예: BTN_SAVE).
   * @param fallback - 번역 미존재 시 사용할 기본 문자열. 미지정 시 key 그대로.
   */
  const label = useCallback(
    (key: string, fallback?: string): string => {
      const val = t(key, { defaultValue: fallback ?? key })
      return typeof val === 'string' ? val : String(val)
    },
    [t]
  )

  /**
   * 백엔드 fw_label 테이블 로더 — addResourceBundle 로 병합.
   * @param locale - 로드할 locale (생략 시 현재 i18n.language).
   */
  const loadLabels = useCallback(
    async (locale?: string) => {
      const lng = locale ?? i18n.language
      try {
        const res = await http.get(`/labels`, { params: { locale: lng } })
        const bundle = res.data?.data ?? res.data ?? {}
        i18n.addResourceBundle(lng, 'translation', bundle, true, true)
      } catch {
        /* noop */
      }
    },
    [i18n]
  )

  return { t: label, label, loadLabels, i18n }
}
